import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Peer from "simple-peer";
import LiveChat from "./LiveChat";
import FloatingReactions, { ReactionButtons } from "./FloatingReactions";
import "./live.css";

export default function LiveViewer({
  liveId,
  host,
  user,
  socket,
  viewerCount,
  chat,
  reactions,
  settings,
  onLeave,
  onChatMessage,
  onReaction,
}) {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState(null);

  const remoteVideoRef = useRef(null);
  const hostPeerRef = useRef(null);

  // Criar conexão peer com o host
  const createHostPeer = useCallback(() => {
    const peer = new Peer({
      initiator: false,
      trickle: true,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      },
    });

    peer.on("signal", (signal) => {
      socket.emit("live:signal", {
        liveId,
        targetSocketId: null, // Será preenchido pelo servidor
        signal,
      });
    });

    peer.on("stream", (stream) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
      setIsConnected(true);
      setIsConnecting(false);
    });

    peer.on("error", (err) => {
      console.error("Peer error:", err);
      setError("Erro de conexão: " + err.message);
      setIsConnecting(false);
    });

    peer.on("close", () => {
      setIsConnected(false);
    });

    hostPeerRef.current = peer;
    return peer;
  }, [liveId, socket]);

  // Sair da live
  const handleLeave = useCallback(() => {
    if (hostPeerRef.current) {
      hostPeerRef.current.destroy();
      hostPeerRef.current = null;
    }

    socket.emit("live:leave", { liveId });

    if (onLeave) onLeave();
  }, [liveId, socket, onLeave]);

  // Configurar socket listeners
  useEffect(() => {
    if (!socket) return;

    // Entrar na live
    socket.emit("live:join", { liveId });

    const handleJoined = ({ hostSocketId }) => {
      // Criar peer quando entrar
      if (!hostPeerRef.current) {
        createHostPeer();
      }
    };

    const handleSignal = ({ fromSocketId, signal }) => {
      if (hostPeerRef.current) {
        hostPeerRef.current.signal(signal);
      } else {
        // Criar peer se não existir
        const peer = createHostPeer();
        peer.signal(signal);
      }
    };

    const handleEnded = ({ reason }) => {
      setIsConnected(false);
      setError(
        reason === "host_disconnected"
          ? "O host encerrou a live"
          : "A live foi encerrada"
      );

      if (hostPeerRef.current) {
        hostPeerRef.current.destroy();
        hostPeerRef.current = null;
      }
    };

    const handleBlocked = () => {
      setError("Você foi bloqueado desta live");
      handleLeave();
    };

    socket.on("live:joined", handleJoined);
    socket.on("live:signal", handleSignal);
    socket.on("live:ended", handleEnded);
    socket.on("live:blocked", handleBlocked);

    return () => {
      socket.off("live:joined", handleJoined);
      socket.off("live:signal", handleSignal);
      socket.off("live:ended", handleEnded);
      socket.off("live:blocked", handleBlocked);

      // Cleanup peer
      if (hostPeerRef.current) {
        hostPeerRef.current.destroy();
        hostPeerRef.current = null;
      }

      // Notificar que saiu
      socket.emit("live:leave", { liveId });
    };
  }, [socket, liveId, createHostPeer, handleLeave]);

  return (
    <div className="live_viewer_container">
      <div className="live_video_section">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="live_video"
        />

        {/* Overlay de informações */}
        <div className="live_overlay">
          <div className="live_status_bar">
            <div className="live_badge_large">
              <span className="live_dot"></span>
              AO VIVO
            </div>
            <div className="live_viewers_count">
              <i className="fa fa-eye"></i>
              {viewerCount}
            </div>
          </div>

          {/* Info do host */}
          <Link to={`/profile/${host?.username}`} className="live_host_info">
            <img src={host?.picture} alt="" className="host_avatar" />
            <span className="host_name">
              {host?.first_name} {host?.last_name}
            </span>
          </Link>

          <FloatingReactions reactions={reactions} />

          {/* Estados de loading/error */}
          {isConnecting && (
            <div className="live_connecting">
              <div className="spinner"></div>
              <p>Conectando à live...</p>
            </div>
          )}

          {error && (
            <div className="live_error_overlay">
              <i className="fa fa-exclamation-circle"></i>
              <p>{error}</p>
              <button onClick={onLeave} className="btn_back">
                Voltar
              </button>
            </div>
          )}
        </div>

        {/* Controles */}
        <div className="live_controls viewer_controls">
          <button className="live_leave_btn" onClick={handleLeave}>
            <i className="fa fa-sign-out"></i>
            Sair
          </button>
        </div>
      </div>

      <div className="live_sidebar">
        {settings?.reactionsEnabled && (
          <div className="live_reactions_section">
            <ReactionButtons onReact={onReaction} disabled={!isConnected} />
          </div>
        )}

        <LiveChat
          chat={chat}
          onSendMessage={onChatMessage}
          disabled={!isConnected || !settings?.chatEnabled}
          user={user}
        />
      </div>
    </div>
  );
}
